/**
 * navigation.ts
 *
 * Navigation seam for Pinia stores.
 *
 * Stores must not `import router from "@/router"`: that module builds a real
 * `createWebHistory()` router at import time and registers navigation guards
 * that call back into `useAuthStore()`, so importing any such store drags the
 * whole router into the module graph - including in tests.
 *
 * `useRouter()` is not a substitute here, because some of these stores are
 * first instantiated outside a component injection context (from a router
 * guard, or from a Connect interceptor handling a 401).
 *
 * The app registers the real router once at startup; tests register a fake.
 * This mirrors how `clients.ts` handles the client registry.
 */
import type { Router } from "vue-router";

let router: Router | null = null;

/**
 * Registers the application router. Called once from `registerPlugins`.
 *
 * @param instance - The application's router.
 */
export function setRouter(instance: Router): void {
  router = instance;
}

/**
 * Returns the registered router.
 *
 * @throws When no router has been registered.
 */
export function getRouter(): Router {
  if (!router) {
    throw new Error(
      "No router registered. In the app this is set up by registerPlugins(); " +
        "in a test, call setRouter() with a fake.",
    );
  }
  return router;
}

/**
 * Navigates to a path.
 *
 * @param path - Target route path.
 */
export function navigate(path: string): Promise<unknown> {
  return getRouter().push(path);
}

/** Resolves once the router has completed its initial navigation. */
export function routerReady(): Promise<void> {
  return getRouter().isReady();
}
